/** Suppliers — the simplest CRUD in the purchasing module, gated `supplier.view`/`supplier.manage`. */

import { api } from '../api.js';
import { supplierListTag, supplierTag } from '../tags.js';
import type { Paged, Supplier, SupplierId } from '../../types/api.js';

export type ListSuppliersParams = {
  readonly search?: string | undefined;
  readonly includeInactive?: boolean | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type SupplierBody = {
  readonly name: string;
  readonly phone?: string | undefined;
  readonly email?: string | undefined;
  readonly kraPin?: string | undefined;
  readonly address?: string | undefined;
  readonly note?: string | undefined;
};

export const suppliersApi = api.injectEndpoints({
  endpoints: (build) => ({
    listSuppliers: build.query<Paged<Supplier>, ListSuppliersParams>({
      query: (params) => ({ url: '/suppliers', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: (result) => [
        supplierListTag(),
        ...(result?.items ?? []).map((supplier) => supplierTag(supplier.id)),
      ],
    }),

    createSupplier: build.mutation<SupplierId, SupplierBody>({
      query: (body) => ({ url: '/suppliers', method: 'POST', body }),
      invalidatesTags: [supplierListTag()],
    }),

    updateSupplier: build.mutation<void, { id: SupplierId; body: SupplierBody }>({
      query: ({ id, body }) => ({ url: `/suppliers/${id}`, method: 'PUT', body }),
      invalidatesTags: (_result, _error, { id }) => [supplierTag(id), supplierListTag()],
    }),

    setSupplierActive: build.mutation<void, { id: SupplierId; isActive: boolean }>({
      query: ({ id, isActive }) => ({
        url: `/suppliers/${id}/activation`,
        method: 'POST',
        body: { isActive },
      }),
      invalidatesTags: (_result, _error, { id }) => [supplierTag(id), supplierListTag()],
    }),
  }),
});

export const {
  useListSuppliersQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useSetSupplierActiveMutation,
} = suppliersApi;
